import React, { useRef, useState } from 'react'
import { cn } from '~/shadcn/lib/utils'
import {
  AccordionItem,
  AccordionContent,
  AccordionTrigger,
} from '~/shadcn/ui/accordion'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { useAccordion } from '~/@mid_ui/accordion/useAccordion'

type DetailItemRow = { heading: React.ReactNode; value: React.ReactNode }

type Props = {
  title: string
  rows: Array<DetailItemRow>
  customContent?: React.ReactNode
  extraContent?: React.ReactNode
  borderBottom?: string
  isParameters?: boolean
  sectionId: string
}

function DetailItem({
  title,
  rows,
  customContent,
  extraContent,
  borderBottom = '1px solid #969696',
  isParameters = false,
  sectionId,
}: Props) {
  const isDesktop = useIsDesktop()
  const { toggleSection } = useAccordion()
  const [stickTab, setStickTab] = useState(true)
  const lastScrollYRef = useRef(0)

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const scrollingUp = currentScrollY < lastScrollYRef.current
      const scrollOffset = 100

      if (scrollingUp && currentScrollY > scrollOffset) {
        setStickTab(true)
      } else if (!scrollingUp) {
        setStickTab(false)
      }

      lastScrollYRef.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (isDesktop) {
    return (
      <div className="flex flex-col">
        <div
          className={cn(
            'px-5 pt-7 pb-5 lg:px-10 lg:py-5 border-b border-border'
          )}>
          <TitleElement
            title={title}
            color={isParameters ? '#FFFFFF' : 'inherit'}
          />
        </div>
        <ContentWrapper
          className="py-10!"
          borderBottom={isParameters ? 'none' : borderBottom}>
          <ContentItemsWrapper>
            {customContent != null ? (
              <div className="flex flex-col">{customContent}</div>
            ) : (
              rows.map((item, index) => (
                <div key={index} className="flex flex-col">
                  {item.heading}
                  <div
                    className={cn(
                      'font-semibold text-end',
                      isParameters && 'md:text-primary-foreground'
                    )}>
                    {item.value}
                  </div>
                </div>
              ))
            )}
            {extraContent && (
              <div className="flex flex-col">{extraContent}</div>
            )}
          </ContentItemsWrapper>
        </ContentWrapper>
      </div>
    )
  }

  // Na mobile používame AccordionItem a AccordionContent
  return (
    <AccordionItem value={sectionId} className="border-0">
      <div
        className={cn(
          'px-5 pt-7 pb-5',
          'z-1',
          'sticky',
          stickTab ? 'top-[91px]' : 'top-0',
          'bg-[rgb(245,245,245)]'
        )}
        style={{ borderBottom: '1px solid #969696' }}>
        <AccordionTrigger
          className="w-full p-0 h-auto hover:no-underline hover:bg-transparent focus-visible:ring-0"
          onClick={() => toggleSection(sectionId)}>
          <div className="flex flex-row justify-between items-center w-full">
            <TitleElement title={title} color="#000" />
          </div>
        </AccordionTrigger>
      </div>

      <AccordionContent
        className={cn('p-5 border-b border-border')}
        style={{
          borderBottom: isParameters ? 'none' : borderBottom,
        }}>
        <ContentItemsWrapper>
          {customContent != null ? (
            <div className="flex flex-col">{customContent}</div>
          ) : (
            rows.map((item, index) => (
              <div key={index} className="flex flex-col">
                {item.heading}
                <div
                  className={cn(
                    'text-base font-semibold text-primary md:text-primary-foreground'
                  )}>
                  {item.value}
                </div>
              </div>
            ))
          )}
          {extraContent && <div className="flex flex-col">{extraContent}</div>}
        </ContentItemsWrapper>
      </AccordionContent>
    </AccordionItem>
  )
}

function TitleElement({ title, color }: { title: string; color: string }) {
  return (
    <h2
      className={cn(
        'text-2xl lg:text-[32px]',
        'break-words overflow-wrap-anywhere',
        'whitespace-normal',
        'text-left',
        'leading-9',
        'font-semibold',
        'lg:text-[inherit]'
      )}
      style={{ color: color !== 'inherit' ? color : undefined }}>
      {title}
    </h2>
  )
}

function ContentWrapper({
  className,
  children,
  borderBottom,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { borderBottom?: string }) {
  return (
    <div
      className={cn('p-5', 'lg:p-10 lg:py-0', className)}
      style={{ borderBottom }}
      {...props}>
      {children}
    </div>
  )
}

function ContentItemsWrapper({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-col',
        '[&>div]:py-1.5',
        'lg:[&>div]:py-1.5',
        '[&>div]:flex [&>div]:flex-row [&>div]:justify-between',
        className
      )}
      {...props}>
      {children}
    </div>
  )
}

export { type DetailItemRow, DetailItem }
